import { cn } from '@/lib/utils';

/**
 * MultiSelect chips style Stripe - tableau de boutons cliquables.
 * Pattern : « Tous » + N options. `selected.length === 0` ⇔ aucune option
 * sélectionnée ⇔ « Tous » actif (= pas de filtre).
 *
 * Différence avec `MultiSelect` du module kpi-consolides : accepte des
 * options `{ value, label }` (utile pour mapper UUID → libellé), labels
 * en français côté UI mais valeurs côté API restent stables.
 */
export interface MultiSelectChipsOption {
  value: string;
  label: string;
}

export interface MultiSelectChipsProps {
  options: ReadonlyArray<MultiSelectChipsOption>;
  selected: string[];
  onChange: (next: string[]) => void;
  /** Label « Tous » personnalisable (ex: « Tous », « Toutes »). */
  allLabel?: string;
  /** Désactive l'ensemble des chips. */
  disabled?: boolean;
}

export function MultiSelectChips({
  options,
  selected,
  onChange,
  allLabel = 'Tous',
  disabled = false,
}: MultiSelectChipsProps) {
  const allSelected = selected.length === 0;

  function toggle(value: string): void {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  }

  const baseClasses =
    'rounded-md px-2.5 py-[5px] text-[11px] font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed';

  return (
    <div className="flex max-h-32 flex-wrap gap-1.5 overflow-y-auto">
      <button
        type="button"
        disabled={disabled}
        onClick={() => onChange([])}
        className={cn(
          baseClasses,
          allSelected
            ? 'bg-accent text-white'
            : 'border border-border-subtle bg-white text-text-muted hover:bg-gray-50',
        )}
      >
        {allLabel}
      </button>
      {options.map((opt) => {
        const isActive = selected.includes(opt.value);
        return (
          <button
            key={opt.value}
            type="button"
            disabled={disabled}
            onClick={() => toggle(opt.value)}
            className={cn(
              baseClasses,
              isActive
                ? 'bg-accent text-white'
                : 'border border-border-subtle bg-white text-text-muted hover:bg-gray-50',
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
